import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Stack,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

interface AlpacaAccountData {
  id: string;
  accountNumber: string;
  status: string;
  buyingPower: string;
  cash: string;
  equity: string;
  portfolioValue: string;
  longMarketValue: string;
  patternDayTrader: boolean;
  tradingBlocked: boolean;
}

const AlpacaConnectionStatus: React.FC = () => {
  const [account, setAccount] = useState<AlpacaAccountData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAccount = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const getAlpacaAccountInfo = httpsCallable<unknown, AlpacaAccountData>(
        functions,
        'getAlpacaAccountInfo'
      );
      const result = await getAlpacaAccountInfo();
      setAccount(result.data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to connect';
      setError(message);
      setAccount(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccount();
  }, [fetchAccount]);

  const formatCurrency = (value: string) => {
    const num = parseFloat(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Connecting to Alpaca...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
        <Tooltip title="Retry connection">
          <IconButton size="small" onClick={fetchAccount}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  if (!account) return null;

  const isActive = account.status === 'ACTIVE' && !account.tradingBlocked;

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Chip
          label={isActive ? 'Connected' : 'Inactive'}
          color={isActive ? 'success' : 'error'}
          size="small"
          variant="outlined"
        />
        <Typography variant="caption" color="text.secondary">
          Paper Trading Account
        </Typography>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchAccount}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack spacing={0.5}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">Buying Power</Typography>
          <Typography variant="body2" fontWeight="medium">
            {formatCurrency(account.buyingPower)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">Equity</Typography>
          <Typography variant="body2" fontWeight="medium">
            {formatCurrency(account.equity)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">Portfolio Value</Typography>
          <Typography variant="body2" fontWeight="medium">
            {formatCurrency(account.portfolioValue)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">Cash</Typography>
          <Typography variant="body2" fontWeight="medium">
            {formatCurrency(account.cash)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">Long Market Value</Typography>
          <Typography variant="body2" fontWeight="medium">
            {formatCurrency(account.longMarketValue)}
          </Typography>
        </Box>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
        API credentials managed in server environment
      </Typography>
    </Box>
  );
};

export default AlpacaConnectionStatus;
